<template>
  <div class="location-map-viewer">
    <div ref="mapContainer" class="location-map" :class="{ '_hidden': !canShowMap }" />
    <div v-if="!showLocation" class="location-map-viewer__empty">
      Location visibility is turned off.
    </div>
    <div v-else-if="!hasCoordinates" class="location-map-viewer__empty">
      No GPS/location coordinates available for this image.
    </div>
    <div v-if="canShowMap" class="location-map-viewer__coords">
      Lat {{ latitude.toFixed(6) }}, Lon {{ longitude.toFixed(6) }}
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { Map, View, Feature } from 'ol';
import { Tile as TileLayer, Vector as VectorLayer } from 'ol/layer';
import { OSM, Vector as VectorSource } from 'ol/source';
import { Point } from 'ol/geom';
import { fromLonLat } from 'ol/proj';
import { Circle as CircleStyle, Fill, Stroke, Style } from 'ol/style';
import 'ol/ol.css';

const props = defineProps({
  imageObj: {
    type: Object,
    default: () => ({}),
  },
  annotations: {
    type: Array,
    default: () => [],
  },
  showLocation: {
    type: Boolean,
    default: true,
  },
});

const mapContainer = ref(null);
const mapInstance = ref(null);
const markerSource = ref(null);

const toFiniteNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const extractLatLonPair = (input) => {
  if (!input || typeof input !== 'object') {
    return null;
  }

  const lat = toFiniteNumber(input.lat ?? input.latitude ?? input?.gps?.lat ?? input?.gps?.latitude);
  const lon = toFiniteNumber(input.lon ?? input.longitude ?? input?.gps?.lon ?? input?.gps?.longitude);

  if (lat === null || lon === null) {
    return null;
  }

  if (Math.abs(lat) > 90 || Math.abs(lon) > 180) {
    return null;
  }

  return { lat, lon };
};

const isGpsLikeTopic = (annotation) => {
  const type = String(annotation?.type || '').toLowerCase();
  const topic = String(annotation?.topic || annotation?.topic_name || annotation?.source || '').toLowerCase();
  return type === 'gps' || type.startsWith('gps_') || topic === 'gps' || topic.startsWith('gps_');
};

const topicCoordinates = computed(() => {
  if (!Array.isArray(props.annotations) || props.annotations.length === 0) {
    return null;
  }

  for (const annotation of props.annotations) {
    if (!isGpsLikeTopic(annotation)) {
      continue;
    }

    const fromDataJson = extractLatLonPair(annotation?.data_json);
    if (fromDataJson) {
      return fromDataJson;
    }

    const fromAnnotation = extractLatLonPair(annotation);
    if (fromAnnotation) {
      return fromAnnotation;
    }

    if (typeof annotation?.raw === 'string') {
      try {
        const parsedRaw = JSON.parse(annotation.raw);
        const fromRaw = extractLatLonPair(parsedRaw);
        if (fromRaw) {
          return fromRaw;
        }
      } catch (error) {
        // Ignore invalid raw payloads and continue scanning.
      }
    }
  }

  return null;
});

const imageCoordinates = computed(() => {
  const imageObj = props.imageObj || {};
  return extractLatLonPair(imageObj);
});

const resolvedCoordinates = computed(() => {
  const topicCoords = topicCoordinates.value;
  const imageCoords = imageCoordinates.value;

  if (topicCoords && imageCoords && imageCoords.lat === 0 && imageCoords.lon === 0) {
    return topicCoords;
  }

  return imageCoords || topicCoords;
});

const latitude = computed(() => {
  return resolvedCoordinates.value?.lat ?? null;
});

const longitude = computed(() => {
  return resolvedCoordinates.value?.lon ?? null;
});

const hasCoordinates = computed(() => latitude.value !== null && longitude.value !== null);
const canShowMap = computed(() => props.showLocation && hasCoordinates.value);

const markerStyle = new Style({
  image: new CircleStyle({
    radius: 6,
    fill: new Fill({ color: '#ff3b30' }),
    stroke: new Stroke({ color: '#ffffff', width: 2 }),
  }),
});

function updateMapLocation() {
  if (!mapInstance.value || !markerSource.value) {
    return;
  }

  markerSource.value.clear();

  if (!canShowMap.value) {
    return;
  }

  const center = fromLonLat([longitude.value, latitude.value]);
  const marker = new Feature({ geometry: new Point(center) });
  marker.setStyle(markerStyle);
  markerSource.value.addFeature(marker);

  mapInstance.value.getView().setCenter(center);
  mapInstance.value.getView().setZoom(16);
}

onMounted(() => {
  markerSource.value = new VectorSource();

  const markerLayer = new VectorLayer({
    source: markerSource.value,
  });

  mapInstance.value = new Map({
    target: mapContainer.value,
    layers: [
      new TileLayer({ source: new OSM() }),
      markerLayer,
    ],
    view: new View({
      center: fromLonLat([0, 0]),
      zoom: 2,
    }),
  });

  updateMapLocation();
});

watch(
  () => [props.imageObj, props.annotations, props.showLocation, latitude.value, longitude.value],
  () => {
    updateMapLocation();
  },
  { deep: true },
);

onUnmounted(() => {
  if (mapInstance.value) {
    mapInstance.value.setTarget(undefined);
    mapInstance.value = null;
  }
});
</script>

<style lang="scss" scoped>
.location-map-viewer {
  position: relative;
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
}

.location-map {
  width: 100%;
  height: 100%;
  min-height: 0;

  &._hidden {
    visibility: hidden;
  }
}

.location-map-viewer__empty {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  text-align: center;
  font-size: 0.95rem;
  font-weight: 600;
  @include themify() {
    color: themed('body-text-color');
    background: themed('background-color2');
  }
}

.location-map-viewer__coords {
  position: absolute;
  left: 12px;
  bottom: 12px;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 0.8rem;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.92);
  color: #222;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}
</style>